import { Entity } from "@common/types/entity";
import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import {
    extractRowActionKey,
    extractRowActionValue,
    RowActionValue,
    stringifyActionValue,
} from "./seedActionRow";
import { ActionRowDefinition } from "./types";
import { resolveRowConditions, ResolvedRowCondition } from "./rowMatching";

export interface ApplyActionAssignment {
    transactionId: number;
    value: BulkUpdateParams["value"];
}

export interface ApplyActionsPlan {
    totalMatchedTransactions: number;
    unsyncedTransactionsCount: number;
    assignments: ApplyActionAssignment[];
}

export type FetchMatchingTransactionIdsForConditions = (
    conditions: ResolvedRowCondition[],
) => Promise<Set<number>>;

interface BuildApplyActionsPlanParams {
    rows: ActionRowDefinition[];
    scopeConditions: ActionRowDefinition["conditions"];
    rowIdToOwnedTxIds: Map<string, number[]>;
    valueToAssign: BulkUpdateParams;
    newValuesMap: Record<number, BulkUpdateParams["value"]>;
    entities: Entity[];
    fetchMatchingTransactionIdsForConditions: FetchMatchingTransactionIdsForConditions;
}

export function getBulkValueForAction({
    update,
    value,
    entities,
}: {
    update: BulkUpdateParams["update"];
    value: RowActionValue;
    entities: Entity[];
}): BulkUpdateParams["value"] | undefined {
    switch (update) {
        case "categoryId":
        case "memo":
            return typeof value === "string" ? value : undefined;
        case "counterpartyId":
        case "coa":
        case "accrualCoa":
            return typeof value === "string" || value === null
                ? value
                : undefined;
        case "classes":
            return Array.isArray(value) ? value : undefined;
        case "entity":
            return typeof value === "number"
                ? entities.find((entity) => entity.id === value)
                : undefined;
        default:
            return undefined;
    }
}

function getCurrentValueKey({
    transactionId,
    valueToAssign,
    newValuesMap,
}: {
    transactionId: number;
    valueToAssign: BulkUpdateParams;
    newValuesMap: Record<number, BulkUpdateParams["value"]>;
}): string {
    const hasNewValue = Object.prototype.hasOwnProperty.call(
        newValuesMap,
        transactionId,
    );
    const currentValue = hasNewValue
        ? newValuesMap[transactionId]
        : valueToAssign.value;
    return stringifyActionValue(valueToAssign.update, currentValue);
}

/**
 * Builds the drawer-level "apply actions" plan with the same top-to-bottom
 * priority as rule execution. Condition-free rows do not match transactions,
 * but when they are above condition rows they protect their current buckets.
 */
export async function buildApplyActionsPlan({
    rows,
    scopeConditions,
    rowIdToOwnedTxIds,
    valueToAssign,
    newValuesMap,
    entities,
    fetchMatchingTransactionIdsForConditions,
}: BuildApplyActionsPlanParams): Promise<ApplyActionsPlan> {
    const reservedTransactionIds = new Set<number>();
    const assignments: ApplyActionAssignment[] = [];
    let totalMatchedTransactions = 0;
    let isBaseActionProtected = false;
    const baseActionKey =
        rows
            .map((row) =>
                row.isBaseAction
                    ? extractRowActionKey(valueToAssign.update, row)
                    : undefined,
            )
            .find((key) => key !== undefined) ??
        stringifyActionValue(valueToAssign.update, valueToAssign.value);

    for (const row of rows) {
        const rowValue = extractRowActionValue(valueToAssign.update, row);

        if (row.conditions.length === 0) {
            if (row.isBaseAction && rowValue !== undefined) {
                isBaseActionProtected = true;
                continue;
            }

            for (const id of rowIdToOwnedTxIds.get(row.id) ?? []) {
                reservedTransactionIds.add(id);
            }
            continue;
        }

        if (rowValue === undefined) {
            continue;
        }

        const bulkValue = getBulkValueForAction({
            update: valueToAssign.update,
            value: rowValue,
            entities,
        });
        if (bulkValue === undefined) {
            continue;
        }

        const conditions = resolveRowConditions({ row, scopeConditions });
        if (conditions.length === 0) {
            continue;
        }

        const matchingTransactionIds =
            await fetchMatchingTransactionIdsForConditions(conditions);
        const rowTransactionIds: number[] = [];
        for (const transactionId of matchingTransactionIds) {
            if (reservedTransactionIds.has(transactionId)) {
                continue;
            }
            if (
                isBaseActionProtected &&
                getCurrentValueKey({
                    transactionId,
                    valueToAssign,
                    newValuesMap,
                }) === baseActionKey
            ) {
                continue;
            }
            rowTransactionIds.push(transactionId);
        }

        for (const transactionId of rowTransactionIds) {
            reservedTransactionIds.add(transactionId);
        }

        totalMatchedTransactions += rowTransactionIds.length;
        const desiredValueKey = stringifyActionValue(
            valueToAssign.update,
            bulkValue,
        );
        for (const transactionId of rowTransactionIds) {
            const currentValueKey = getCurrentValueKey({
                transactionId,
                valueToAssign,
                newValuesMap,
            });
            if (currentValueKey === desiredValueKey) {
                continue;
            }
            assignments.push({ transactionId, value: bulkValue });
        }
    }

    return {
        totalMatchedTransactions,
        unsyncedTransactionsCount: assignments.length,
        assignments,
    };
}
